// frontend/src/components/Dashboard/OrderDetailsModal.jsx - CLEANED
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faBox,
  faDollarSign,
  faTruck,
  faCalendar,
  faEdit,
  faCheckCircle,
  faClock,
  faTractor,
  faCreditCard,
  faSpinner,
  faSave,
  faUndo,
  faInfoCircle,
  faClipboard,
  faReceipt,
  faShoppingCart,
  faWallet,
  faMobileAlt,
  faBuilding,
  faGlobe,
  faWeightScale,
  faBoxes,
  faTag,
  faShip,
  faAnchor,
  faWhatsapp,
  faLocationDot,
  faCheckDouble,
  faRocket,
  faExclamationTriangle,
  faShieldAlt,
  faStore,
  faReceipt as faReceiptIcon,
  faClipboardList,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";

// ============================================
// CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
      return faCheckDouble;
    case "cancelled":
      return faTimes;
    case "out_for_delivery":
      return faRocket;
    case "shipped":
      return faShip;
    default:
      return faClock;
  }
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    processing: "Processing",
    confirmed: "Confirmed",
    shipped: "Shipped",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
    refunded: "Refunded",
  };
  return labels[status] || status;
};

const getPaymentMethodLabel = (method) => {
  const labels = {
    cash_on_delivery: "Cash on Delivery",
    bank_transfer: "Bank Transfer",
    mobile_money: "Mobile Money",
    credit: "Credit",
    card: "Credit/Debit Card",
    orange_money: "Orange Money",
    afrimoney: "Afrimoney",
    wave: "WAVE",
  };
  return labels[method] || method?.replace(/_/g, " ") || "N/A";
};

const getPaymentStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    processing: "Processing",
    paid: "Paid",
    failed: "Failed",
    refunded: "Refunded",
    deposit_received: "Deposit Received",
  };
  return labels[status] || status || "N/A";
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

// ============================================
// MAIN COMPONENT
// ============================================
const OrderDetailsModal = ({ order, onClose, onUpdateStatus, isAdmin }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(order?.status || "");
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    shipping: true,
    items: true,
    timeline: true,
    payment: true,
    notes: true,
    farmer: false,
    supplier: true,
  });

  if (!order) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleStatusUpdate = async () => {
    if (selectedStatus === order.status) {
      setEditing(false);
      return;
    }
    setLoading(true);
    try {
      await onUpdateStatus(order._id, selectedStatus);
      setEditing(false);
    } catch (error) {
      console.error("Error updating status:", error);
      alert(error.response?.data?.message || "Failed to update order status");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedStatus(order.status);
    setEditing(false);
  };

  const isBulkOrder =
    order.orderType === "bulk_farmer" ||
    (order.totalQuantity && order.totalQuantity >= 100);
  const isCreditOrder =
    order.payment?.method === "credit" ||
    order.farmerDetails?.creditTerms?.isCreditOrder;
  const isFromMalaysia =
    order.origin === SUPPLIER.country ||
    order.supplier?.country === SUPPLIER.country;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content order-details-modal large"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faReceiptIcon} /> Order Details -{" "}
            {order.orderNumber}
            {isBulkOrder && (
              <span className="badge bulk">
                <FontAwesomeIcon icon={faTractor} /> BULK
              </span>
            )}
            {isCreditOrder && (
              <span className="badge credit">
                <FontAwesomeIcon icon={faCreditCard} /> Credit
              </span>
            )}
            {isFromMalaysia && (
              <span className="badge malaysia">
                <FontAwesomeIcon icon={faShip} /> {SUPPLIER.country}
              </span>
            )}
            {order.source && (
              <span className="badge source">
                <FontAwesomeIcon icon={faGlobe} /> {order.source}
              </span>
            )}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {/* ========== STATUS SECTION ========== */}
          <div
            className="status-section"
            style={{ borderLeftColor: getStatusColor(order.status) }}
          >
            <div className="status-header">
              <div className="status-display">
                <FontAwesomeIcon
                  icon={getStatusIcon(order.status)}
                  style={{ color: getStatusColor(order.status) }}
                />
                <span
                  className="status-label"
                  style={{ color: getStatusColor(order.status) }}
                >
                  {getStatusLabel(order.status)}
                </span>
                <span className="order-date">
                  <FontAwesomeIcon icon={faCalendar} />{" "}
                  {formatDate(order.createdAt)}
                </span>
                {order.ageInDays > 0 && (
                  <span className="order-age">
                    {order.ageInDays} day{order.ageInDays > 1 ? "s" : ""} old
                  </span>
                )}
                {isFromMalaysia && (
                  <span className="origin-tag">
                    <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="status-actions">
                  {!editing ? (
                    <button
                      className="btn-edit"
                      onClick={() => setEditing(true)}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Update Status
                    </button>
                  ) : (
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      <FontAwesomeIcon icon={faUndo} /> Cancel
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAdmin && editing && (
              <div className="status-update-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="shipped">Shipped</option>
                      <option value="out_for_delivery">Out for Delivery</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={handleStatusUpdate}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> Update Status
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ========== SUPPLIER INFORMATION ========== */}
          <div className="detail-section supplier">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("supplier")}
            >
              <h3>
                <FontAwesomeIcon icon={faShip} /> Supplier Information
                <span className="supplier-badge">
                  <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.supplier ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.supplier && (
              <div className="supplier-details">
                <div className="supplier-info-card">
                  <FontAwesomeIcon icon={faShip} className="supplier-icon" />
                  <div className="supplier-info">
                    <strong>{SUPPLIER.name}</strong>
                    <span>
                      <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}{" "}
                      &bull;
                      <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                    </span>
                  </div>
                  <span className="supplier-badge">
                    <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
                  </span>
                </div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Origin:</span>
                    <span className="detail-value">
                      <span className="origin-tag">
                        <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
                      </span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Port of Loading:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Port of Discharge:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faAnchor} />{" "}
                      {SUPPLIER.portDischarge}
                    </span>
                  </div>
                  {order.shipping?.vesselName && (
                    <div className="detail-item">
                      <span className="detail-label">Vessel:</span>
                      <span className="detail-value">
                        <FontAwesomeIcon icon={faShip} />{" "}
                        {order.shipping.vesselName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ========== CUSTOMER INFORMATION ========== */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("customer")}
            >
              <h3>
                <FontAwesomeIcon icon={faUser} /> Customer Information
              </h3>
              <span className="toggle-icon">
                {expandedSections.customer ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.customer && (
              <div className="customer-details">
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">
                      {order.userName ||
                        order.customerName ||
                        order.user?.name ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faEnvelope} /> Email:
                    </span>
                    <span className="detail-value">
                      {order.userEmail ||
                        order.customerEmail ||
                        order.user?.email ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faPhone} /> Phone:
                    </span>
                    <span className="detail-value">
                      <a
                        href={`tel:${order.userPhone || order.customerPhone || order.user?.phone}`}
                        className="phone-link"
                      >
                        {order.userPhone ||
                          order.customerPhone ||
                          order.user?.phone ||
                          "N/A"}
                      </a>
                    </span>
                  </div>
                  {order.customerWhatsapp && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp:
                      </span>
                      <span className="detail-value">
                        <a
                          href={`https://wa.me/${order.customerWhatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-link"
                        >
                          <FontAwesomeIcon icon={faWhatsappBrand} />{" "}
                          {order.customerWhatsapp}
                        </a>
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">User Type:</span>
                    <span className={`user-type-badge ${order.userType}`}>
                      {order.userType === "farmer" ? "Farmer" : "Customer"}
                    </span>
                  </div>
                  {order.farmerDetails?.farmName && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faTractor} /> Farm:
                      </span>
                      <span className="detail-value">
                        {order.farmerDetails.farmName}
                      </span>
                    </div>
                  )}
                  {order.farmerDetails?.farmSize && (
                    <div className="detail-item">
                      <span className="detail-label">Farm Size:</span>
                      <span className="detail-value">
                        {order.farmerDetails.farmSize.charAt(0).toUpperCase() +
                          order.farmerDetails.farmSize.slice(1)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ========== SHIPPING ADDRESS ========== */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("shipping")}
            >
              <h3>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
              </h3>
              <span className="toggle-icon">
                {expandedSections.shipping ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.shipping && (
              <div className="shipping-details">
                {order.shippingAddress ? (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Recipient:</span>
                      <span className="detail-value">
                        {order.shippingAddress.recipientName || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">
                        {order.shippingAddress.street}
                        {order.shippingAddress.city &&
                          `, ${order.shippingAddress.city}`}
                        {order.shippingAddress.district &&
                          `, ${order.shippingAddress.district}`}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Country:</span>
                      <span className="detail-value">
                        <span className="destination-tag">
                          🇸🇱 {order.shippingAddress.country || "Sierra Leone"}
                        </span>
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faPhone} /> Phone:
                      </span>
                      <span className="detail-value">
                        {order.shippingAddress.phone || "N/A"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="no-data">No shipping address provided</p>
                )}
              </div>
            )}
          </div>

          {/* ========== ORDER ITEMS ========== */}
          <div className="detail-section items">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("items")}
            >
              <h3>
                <FontAwesomeIcon icon={faBox} /> Order Items
                <span className="item-count">
                  ({order.totalItems || order.items?.length || 0} items,{" "}
                  {order.totalQuantity || 0} kg)
                </span>
                <span className="origin-summary">
                  <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.items ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.items && (
              <div className="table-responsive">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Origin</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div className="product-info">
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="item-thumb"
                              />
                            )}
                            <span className="item-name">{item.name}</span>
                            {item.priceTier && item.priceTier !== "retail" && (
                              <span className="tier-badge">
                                {item.priceTier.replace("_", " ")}
                              </span>
                            )}
                            {item.origin === SUPPLIER.country && (
                              <span className="origin-tag">🇲🇾</span>
                            )}
                          </div>
                        </td>
                        <td>{item.quantity}</td>
                        <td>{item.unit || "kg"}</td>
                        <td>${(item.price?.usd || 0).toFixed(2)}</td>
                        <td>
                          {item.discountPercent > 0 ? (
                            <span className="discount-badge">
                              -{item.discountPercent}%
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {item.origin || SUPPLIER.country}
                          </span>
                        </td>
                        <td>${(item.totalPrice?.usd || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5"></td>
                      <td className="total-label">Subtotal:</td>
                      <td className="total-value">
                        ${(order.subtotal?.usd || 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="5"></td>
                      <td className="total-label">Shipping:</td>
                      <td className="total-value">
                        ${(order.shippingCost?.usd || 0).toFixed(2)}
                      </td>
                    </tr>
                    {order.discount?.usd > 0 && (
                      <tr>
                        <td colSpan="5"></td>
                        <td className="total-label discount">Discount:</td>
                        <td className="total-value discount">
                          -${(order.discount?.usd || 0).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {order.tax?.usd > 0 && (
                      <tr>
                        <td colSpan="5"></td>
                        <td className="total-label">Tax:</td>
                        <td className="total-value">
                          ${(order.tax?.usd || 0).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    <tr className="grand-total">
                      <td colSpan="5"></td>
                      <td className="total-label">
                        <strong>Total:</strong>
                      </td>
                      <td className="total-value">
                        <strong>
                          $
                          {(
                            order.total?.usd ||
                            order.totalAmount?.usd ||
                            0
                          ).toFixed(2)}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* ========== ORDER TIMELINE ========== */}
          <div className="detail-section timeline">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("timeline")}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendar} /> Order Timeline
                <span className="timeline-count">
                  ({order.timeline?.length || 1} events)
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.timeline ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.timeline && (
              <div className="timeline">
                {order.timeline?.length > 0 ? (
                  order.timeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div
                        className="timeline-dot"
                        style={{ background: getStatusColor(event.status) }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-status">
                          {getStatusLabel(event.status)}
                          {event.updatedBy && (
                            <span className="updated-by">
                              by {event.updatedBy}
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <div className="timeline-description">
                            {event.description}
                          </div>
                        )}
                        <div className="timeline-date">
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="timeline-item">
                    <div
                      className="timeline-dot"
                      style={{ background: "#D9A321" }}
                    />
                    <div className="timeline-content">
                      <div className="timeline-status">Order Created</div>
                      <div className="timeline-date">
                        {formatDate(order.createdAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ========== PAYMENT INFORMATION ========== */}
          {order.payment && (
            <div className="detail-section payment">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("payment")}
              >
                <h3>
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Information
                </h3>
                <span className="toggle-icon">
                  {expandedSections.payment ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.payment && (
                <div className="payment-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Method:</span>
                      <span className="detail-value">
                        {getPaymentMethodLabel(order.payment.method)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span
                        className={`payment-status-badge ${order.payment.status}`}
                      >
                        {getPaymentStatusLabel(order.payment.status)}
                      </span>
                    </div>
                    {order.payment.transactionId && (
                      <div className="detail-item">
                        <span className="detail-label">Transaction ID:</span>
                        <span className="detail-value">
                          {order.payment.transactionId}
                        </span>
                      </div>
                    )}
                    {order.payment.mobileMoneyNumber && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faMobileAlt} /> Mobile Money:
                        </span>
                        <span className="detail-value">
                          {order.payment.mobileMoneyNumber}
                        </span>
                      </div>
                    )}
                    {order.payment.bankReference && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faBuilding} /> Bank Ref:
                        </span>
                        <span className="detail-value">
                          {order.payment.bankReference}
                        </span>
                      </div>
                    )}
                    {order.payment.depositAmount?.usd > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Deposit:</span>
                        <span className="detail-value">
                          ${order.payment.depositAmount.usd.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {order.payment.balanceDue?.usd > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Balance Due:</span>
                        <span className="detail-value balance-due">
                          ${order.payment.balanceDue.usd.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========== NOTES ========== */}
          {(order.customerNotes || order.adminNotes) && (
            <div className="detail-section notes">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("notes")}
              >
                <h3>
                  <FontAwesomeIcon icon={faClipboard} /> Notes
                </h3>
                <span className="toggle-icon">
                  {expandedSections.notes ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.notes && (
                <div className="notes-details">
                  {order.customerNotes && (
                    <div className="note-item customer">
                      <span className="note-label">Customer:</span>
                      <p>{order.customerNotes}</p>
                    </div>
                  )}
                  {order.adminNotes && (
                    <div className="note-item admin">
                      <span className="note-label">Admin:</span>
                      <p>{order.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {order.customerWhatsapp && (
            <a
              href={`https://wa.me/${order.customerWhatsapp}?text=Hi%20${encodeURIComponent(order.userName || order.customerName || "")}%2C%20your%20order%20${encodeURIComponent(order.orderNumber || "")}%20is%20currently%20${encodeURIComponent(getStatusLabel(order.status))}.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsappBrand} /> Contact Customer
            </a>
          )}
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
